import { SpinnerCentered } from "#web/components/common/SpinnerCentered/SpinnerCentered";
import { LeakedCode } from "#web/components/leaks/_models/leakedCode";
import { OrdersUsingCodeWithinWeek } from "#web/components/leaks/_models/ordersUsingCodeWithinWeek";
import { LeakedCodeDetailsQuery } from "#web/components/leaks/LeakedCodeDetailsQuery";
import { LeakStatusBadge } from "#web/components/leaks/LeakStatusBadge";
import { ShopifyStatusBadge } from "#web/components/leaks/ShopifyStatusBadge";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { i18nLike } from "#web/services/i18nLike";
import {
  Banner,
  BlockStack,
  Box,
  Card,
  Divider,
  IndexTable,
  InlineGrid,
  InlineStack,
  Layout,
  Link,
  Page,
  Text,
} from "@shopify/polaris";
import { Annotation, BarChart, PolarisVizProvider } from "@shopify/polaris-viz";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import * as s from "./LeaksDetails.css";

const en = {
  backAction: "Leaks",
  couponUsage: "Coupon usage",
  error: {
    leakedCodeDetailsQuery: {
      content: "Please try again later.",
      title: "Error loading coupon details",
    },
  },
  firstSeen: "First seen",
  installedApp: "Installed app",
  lastSeen: "Last seen",
  leakDiscovered: "Leak discovered",
  leakDiscoveredAndInstalledApp: "Leak discovered on app installation",
  leakSources: "Leak sources",
  leakStatus: "Leak status",
  metadata: "Metadata",
  noCodeName: "No coupon name",
  noMetadata: "No metadata",
  shopifyStatus: "Shopify status",
  source: {
    plural: "Sources",
    singular: "Source",
  },
  title: "{{code}}",
  url: "URL",
  value: "Value",
  weekAfter: {
    plural: "{{count}} weeks after leak discovered",
    singular: "{{count}} week after leak discovered",
  },
  weekBefore: {
    plural: "{{count}} weeks before leak discovered",
    singular: "{{count}} week before leak discovered",
  },
  weeklyUsage: "Weekly usage",
};

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const isLastMetadataKey = (metadata: Record<string, unknown>) => (index: number) => {
  return index === Object.keys(metadata).length - 1;
};

const isInstalledWithin8PastWeeksBeforeLeak = ({
  leakedCode,
  shopInstalledAt,
}: {
  leakedCode: LeakedCode;
  shopInstalledAt: Date;
}) => {
  const daysTolerance = 8 * 7 * 24 * 60 * 60 * 1000;
  return leakedCode.leakedAt.getTime() > shopInstalledAt.getTime() - daysTolerance;
};

const formatWeekStartToEndDate = (weeklyUsage: OrdersUsingCodeWithinWeek) => {
  return `${weeklyUsage.weekStart.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })} - ${weeklyUsage.weekEnd.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
};

export const LeaksDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const {
    data: leakedCodeDetails,
    isError: leakedCodeDetailsError,
    isFetching: leakedCodeDetailsLoading,
  } = useQuery(LeakedCodeDetailsQuery({ id }));

  const NON_DISPLAYABLE_METADATA_KEYS = ["verification_date"];

  const ordersUsingCodePerWeek =
    leakedCodeDetails?.leakedCode.weeklyUsages.map((weeklyUsage) => ({
      key: formatWeekStartToEndDate(weeklyUsage),
      value: weeklyUsage.orders.length,
    })) ?? [];
  const installedAppWeekKey = shop?.createdAt
    ? (leakedCodeDetails?.leakedCode.weeklyUsages.findIndex(
        (week) => week.weekEnd.getTime() >= shop.createdAt.getTime(),
      ) ?? null)
    : null;
  const leakedDateWeekKey =
    leakedCodeDetails?.leakedCode.weeklyUsages.findIndex(
      (week) => week.weekEnd.getTime() >= leakedCodeDetails.leakedCode.leakedAt.getTime(),
    ) ?? null;

  const annotations: Annotation[] =
    leakedCodeDetails && shop?.createdAt
      ? [
          ...(leakedCodeDetails.leakedCode.leakedOnAppInstallation
            ? [
                {
                  axis: "x" as const,
                  label: t("leakDiscoveredAndInstalledApp"),
                  startKey: leakedDateWeekKey ? ordersUsingCodePerWeek[leakedDateWeekKey]!.key : "NO_KEY",
                },
              ]
            : [
                {
                  axis: "x" as const,
                  label: t("leakDiscovered"),
                  startKey: leakedDateWeekKey ? ordersUsingCodePerWeek[leakedDateWeekKey]!.key : "NO_KEY",
                },
              ]),
          ...(isInstalledWithin8PastWeeksBeforeLeak({
            leakedCode: leakedCodeDetails.leakedCode,
            shopInstalledAt: shop.createdAt,
          })
            ? [
                {
                  axis: "x" as const,
                  label: t("installedApp"),
                  startKey: installedAppWeekKey ? ordersUsingCodePerWeek[installedAppWeekKey]!.key : "NO_KEY",
                },
              ]
            : []),
        ]
      : [];

  const formatValue = (leakedCode: LeakedCode) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (leakedCode.value == null) {
      return "N/A";
    }

    return leakedCode.valueType === "percentage"
      ? `${String(Math.abs(leakedCode.value))}%`
      : `${String(Math.abs(leakedCode.value))} ${shop?.currency ?? ""}`;
  };

  const isLoading = leakedCodeDetailsLoading || shopFetching;
  const isError = leakedCodeDetailsError || shopError;

  return (
    <Page
      backAction={{
        content: t("backAction"),
        onAction: () => {
          navigate("/leaks");
        },
      }}
      title={t("title", {
        code: !isLoading ? (leakedCodeDetails?.leakedCode.code ?? t("noCodeName")) : "",
      })}
    >
      {isLoading ? (
        <SpinnerCentered />
      ) : isError ? (
        <Banner title={t("error.leakedCodeDetailsQuery.title")} tone="critical">
          {t("error.leakedCodeDetailsQuery.content")}
        </Banner>
      ) : (
        <Layout>
          <Layout.Section variant="oneHalf">
            <Card>
              <Box minHeight="235">
                <BlockStack gap="300">
                  <InlineGrid columns={2} gap="200">
                    <Text as="h2" variant="headingMd">
                      {t("leakStatus")}
                    </Text>
                    <Box>
                      <LeakStatusBadge leakedCode={leakedCodeDetails!.leakedCode} />
                    </Box>
                  </InlineGrid>
                  <InlineGrid columns={2} gap="200">
                    <Text as="h2" variant="headingMd">
                      {t("shopifyStatus")}
                    </Text>
                    <Box>
                      <ShopifyStatusBadge leakedCode={leakedCodeDetails!.leakedCode} />
                    </Box>
                  </InlineGrid>
                  <InlineGrid columns={2} gap="200">
                    <Text as="h2" variant="headingMd">
                      {t("value")}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {formatValue(leakedCodeDetails!.leakedCode)}
                    </Text>
                  </InlineGrid>
                  <InlineGrid columns={2} gap="200">
                    <Text as="h2" variant="headingMd">
                      {t("firstSeen")}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {leakedCodeDetails!.leakedCode.leakedAt.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </Text>
                  </InlineGrid>
                  <InlineGrid columns={2} gap="200">
                    <Text as="h2" variant="headingMd">
                      {t("lastSeen")}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {leakedCodeDetails!.leakedCode.lastScrapedAt?.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </Text>
                  </InlineGrid>
                  <InlineGrid columns={2} gap="200">
                    <Text as="h2" variant="headingMd">
                      {t("leakSources")}
                    </Text>
                    <InlineStack gap="200">
                      <Text as="p" variant="bodyMd">
                        {leakedCodeDetails!.leakedCode.sources.length}
                      </Text>
                    </InlineStack>
                  </InlineGrid>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          <Layout.Section variant="oneHalf">
            <Card>
              <Box minHeight="235">
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Coupon Usage
                  </Text>
                  <PolarisVizProvider>
                    <Box>
                      <BarChart
                        annotations={annotations}
                        data={[
                          {
                            data: ordersUsingCodePerWeek,
                            name: t("weeklyUsage"),
                          },
                        ]}
                        theme="Light"
                        type="stacked"
                        xAxisOptions={{
                          hide: true,
                          labelFormatter: (value) => String(value),
                        }}
                      />
                    </Box>
                  </PolarisVizProvider>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <Card padding="0">
              <BlockStack>
                <Box padding="400">
                  <Text as="h2" variant="headingMd">
                    {t("leakSources")}
                  </Text>
                </Box>
                <Divider />

                <IndexTable
                  headings={[{ title: t("source.singular") }, { title: t("url") }]}
                  itemCount={leakedCodeDetails!.leakedCode.sources.length}
                  resourceName={{
                    plural: t("source.plural"),
                    singular: t("source.singular"),
                  }}
                  selectable={false}
                >
                  {leakedCodeDetails!.leakedCode.sources.map((source, i) => (
                    <IndexTable.Row id={source.url} key={source.url} position={i}>
                      <IndexTable.Cell>
                        <Text as="p" variant="bodyMd">
                          {source.name}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {source.name !== "Coupon Extension" ? (
                          <Link removeUnderline target="_blank" url={source.url}>
                            <Text as="span" fontWeight="regular" variant="bodyXs">
                              {source.url}
                            </Text>
                          </Link>
                        ) : (
                          <Text as="p" fontWeight="regular" variant="bodyXs">
                            Leaked on a coupon extension
                          </Text>
                        )}
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <Card padding="0">
              <BlockStack>
                <Box padding="400">
                  <Text as="h2" variant="headingMd">
                    {t("metadata")}
                  </Text>
                </Box>
                {leakedCodeDetails!.leakedCode.leakedSiteMetadata ? (
                  Object.entries(leakedCodeDetails!.leakedCode.leakedSiteMetadata)
                    .filter(([key]) => !NON_DISPLAYABLE_METADATA_KEYS.includes(key))
                    .map(([key, value], index) => (
                      <div className={s.MetadataWrapper} key={key}>
                        <Divider />
                        <IndexTable
                          headings={[
                            { title: key },
                            ...(typeof value === "string" ? [{ title: value }] : [{ title: " " }]),
                          ]}
                          itemCount={leakedCodeDetails!.leakedCode.sources.length}
                          resourceName={{
                            plural: t("metadata"),
                            singular: t("metadata"),
                          }}
                          selectable={false}
                        >
                          {isObject(value) &&
                            Object.entries(value)
                              .filter(([_key]) => !NON_DISPLAYABLE_METADATA_KEYS.includes(_key))
                              .map(([_key, _value]) => (
                                <IndexTable.Row id={_key} key={_key} position={index}>
                                  <IndexTable.Cell>
                                    <Text as="p" fontWeight="bold" variant="bodyMd">
                                      {_key}
                                    </Text>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                    {typeof _value === "string" ? (
                                      <Text as="p" variant="bodyMd">
                                        <code>{_value}</code>
                                      </Text>
                                    ) : (
                                      <Text as="p" variant="bodyMd">
                                        <code>{JSON.stringify(_value, null, 2)}</code>
                                      </Text>
                                    )}
                                  </IndexTable.Cell>
                                </IndexTable.Row>
                              ))}
                        </IndexTable>
                        {isLastMetadataKey(leakedCodeDetails!.leakedCode.leakedSiteMetadata!)(index) && <Divider />}
                      </div>
                    ))
                ) : (
                  <Box paddingBlockEnd="400" paddingInline="400">
                    <Text as="p" variant="bodyMd">
                      {t("noMetadata")}
                    </Text>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      )}
    </Page>
  );
};
